import { useState, useEffect, useMemo } from 'react';
import {
    Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Activity, Calendar, AlertTriangle, Info, AlertCircle, CheckCircle2,
    ArrowUp, ArrowDown, ArrowUpDown, Loader2, Server, Clock, Tag, Shield,
    Network, Bot, Ticket, CircleDot,
    SquareArrowOutUpRight, X, XCircle,
    Download, Columns3, Eye, EyeOff, Check
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { useSearchParams } from 'react-router-dom';
import { useClientLevel } from '../../hooks/useClientLevel';
import SingleSelectLocalSearchApiDropdown from '../../components/SingleSelectLocalSearchApiDropdown';
import AgentExportModal from '../Agent/AgentExportModal';



// --- Interfaces ---
interface EventItem {
    id: number;
    event_type: string;
    severity: string;
    message: string;
    description: string;
    event_status: string;
    event_data: string;
    organization_id: number;
    client_id: number;
    asset_id: number | null;
    agent_id: number;
    asset_hostname: string | null;
    asset_ip: string | null;
    agent_name: string;
    client_name: string;
    ticket_id: number | null;
    ticket_number: string | null;
    created: string;
    updated: string;
    is_deleted: boolean;
    [key: string]: any;
}


// --- Helper Functions ---
const getSeverityConfig = (severity: string) => {
    const s = severity?.toLowerCase() || '';
    if (s === 'critical') return { color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', icon: AlertTriangle, dot: 'bg-red-500' };
    if (s === 'high' || s === 'error') return { color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', icon: AlertCircle, dot: 'bg-orange-500' };
    if (s === 'medium' || s === 'warning') return { color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30', icon: AlertCircle, dot: 'bg-yellow-500' };
    if (s === 'low' || s === 'info') return { color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30', icon: Info, dot: 'bg-blue-500' };
    if (s === 'success') return { color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30', icon: CheckCircle2, dot: 'bg-green-500' };
    return { color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30', icon: Info, dot: 'bg-gray-500' };
};

const getStatusConfig = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s === 'open') return { color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30', dot: 'bg-yellow-500' };
    if (s === 'closed') return { color: 'text-green-400', bg: 'bg-green-500/15', border: 'border-green-500/30', dot: 'bg-green-500' };
    return { color: 'text-gray-400', bg: 'bg-gray-500/15', border: 'border-gray-500/30', dot: 'bg-gray-500' };
};

const formatTimestamp = (ts: string) => {
    if (!ts) return '-';
    try {
        const date = new Date(ts);
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
        });
    } catch { return ts; }
};

const getRelativeTime = (ts: string) => {
    if (!ts) return '';
    try {
        const now = new Date();
        const date = new Date(ts);
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `${diffDays}d ago`;
        return `${Math.floor(diffDays / 30)}mo ago`;
    } catch { return ''; }
};

const formatEventType = (type: string) => {
    if (!type) return '-';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};


// --- Main Component ---
const EventsTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const statusFilter = (searchParams.get('event_status') as 'all' | 'open' | 'closed') || 'all';
    const severityFilter = (searchParams.get('severity') as 'all' | 'critical' | 'warning' | 'info') || 'all';
    const datePreset = (searchParams.get('date_preset') as 'all' | '24h' | '7d' | '30d' | 'custom') || 'all';
    const dateFrom = searchParams.get('from_date') || '';
    const dateTo = searchParams.get('to_date') || '';
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [eventsData, setEventsData] = useState<EventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const p = searchParams.get('page');
        return p ? parseInt(p, 10) : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        const l = searchParams.get('limit');
        return l ? parseInt(l, 10) : 30;
    });
    const level = useClientLevel();

    const [sortBy, setSortBy] = useState<string>(() => {
        const urlSortBy = searchParams.get('sort_by');
        if (urlSortBy) return urlSortBy;

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.eventDefaultSortKey) {
                    return parsed.eventDefaultSortKey;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for event sortBy", e);
            }
        }
        return 'created';
    });

    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
        const urlSortOrder = searchParams.get('sort_order') as 'asc' | 'desc';
        if (urlSortOrder) return urlSortOrder;

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.eventDefaultSortDir) {
                    return parsed.eventDefaultSortDir;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for event sortOrder", e);
            }
        }
        return 'desc';
    });

    const [settingsVersion, setSettingsVersion] = useState(0);

    useEffect(() => {
        const handleSettingsChange = () => {
            setSettingsVersion(prev => prev + 1);

            const urlSortBy = searchParams.get('sort_by');
            const urlSortOrder = searchParams.get('sort_order');
            
            const stored = localStorage.getItem('organization_settings');
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    if (!urlSortBy && parsed.eventDefaultSortKey) {
                        setSortBy(parsed.eventDefaultSortKey);
                    }
                    if (!urlSortOrder && parsed.eventDefaultSortDir) {
                        setSortOrder(parsed.eventDefaultSortDir);
                    }
                } catch (e) {
                    console.error("Failed to parse organization_settings", e);
                }
            } else {
                if (!urlSortBy) setSortBy('created');
                if (!urlSortOrder) setSortOrder('desc');
            }
        };
        window.addEventListener('organization-settings-changed', handleSettingsChange);
        return () => window.removeEventListener('organization-settings-changed', handleSettingsChange);
    }, [searchParams]);

    const baseColumns = useMemo(() => {
        const defaultCols = [
            { key: 'asset_hostname', label: 'Asset / Host', icon: Server, color: 'text-cyan-400', sortable: true },
            { key: 'client_name', label: 'Client', icon: Activity, color: 'text-emerald-400', sortable: false },
            { key: 'event_type', label: 'Event Type', icon: Tag, color: 'text-purple-400', sortable: true },
            { key: 'message', label: 'Message', icon: Info, color: 'text-cyan-400', sortable: true },
            { key: 'event_status', label: 'Status', icon: CircleDot, color: 'text-emerald-400', sortable: true },
            { key: 'severity', label: 'Severity', icon: Shield, color: 'text-red-400', sortable: true },
            { key: 'ticket_number', label: 'Ticket', icon: Ticket, color: 'text-orange-400', sortable: true },
            { key: 'created', label: 'Created', icon: Calendar, color: 'text-yellow-400', sortable: true },
            { key: 'actions', label: 'Actions', sortable: false }
        ];

        const stored = localStorage.getItem('organization_settings');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed.eventColumnOrder && Array.isArray(parsed.eventColumnOrder)) {
                    const ordered: typeof defaultCols = [];
                    parsed.eventColumnOrder.forEach((k: string) => {
                        const found = defaultCols.find(c => c.key === k);
                        if (found) ordered.push(found);
                    });
                    defaultCols.forEach(c => {
                        if (!ordered.some(o => o.key === c.key)) {
                            ordered.push(c);
                        }
                    });
                    return ordered;
                }
            } catch (e) {
                console.error("Failed to parse organization_settings for event column ordering", e);
            }
        }
        return defaultCols;
    }, [settingsVersion]);

    type EventColumnKey =
        | 'asset_hostname'
        | 'client_name'
        | 'event_type'
        | 'message'
        | 'event_status'
        | 'severity'
        | 'ticket_number'
        | 'created'
        | 'actions';

    const [showColumnFilter, setShowColumnFilter] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Record<EventColumnKey, boolean>>({
        asset_hostname: true,
        client_name: true,
        event_type: true,
        message: true,
        event_status: true,
        severity: true,
        ticket_number: true,
        created: true,
        actions: true,
    });

    const columnOptions = useMemo(() => {
        return baseColumns
            .filter((col) => !(col.key === 'client_name' && level?.id))
            .map((col) => ({
                key: col.key as EventColumnKey,
                label: col.label,
                icon: col.icon,
                color: col.color,
            }));
    }, [baseColumns, level]);

    const handleColumnToggle = (column: EventColumnKey) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [column]: !prev[column],
        }));
    };

    const toggleAllColumns = () => {
        const availableKeys = columnOptions.map((o) => o.key);
        const allVisible = availableKeys.every((k) => visibleColumns[k]);
        setVisibleColumns((prev) => {
            const next = { ...prev };
            availableKeys.forEach((k) => {
                next[k] = !allVisible;
            });
            return next;
        });
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (showColumnFilter && !target.closest('[data-col-menu]')) {
                setShowColumnFilter(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showColumnFilter]);

    const columns = useMemo(() => {
        return baseColumns.filter((col) => {
            if (col.key === 'client_name' && level?.id) {
                return false;
            }
            if (visibleColumns[col.key as EventColumnKey] === false) {
                return false;
            }
            return true;
        });
    }, [baseColumns, level, visibleColumns]);

    const [selectedEvent, setSelectedEvent] = useState<EventItem | null>(null);
    const [eventTypeOption, setEventTypeOption] = useState<any | null>(null);
    const [closeModalOpen, setCloseModalOpen] = useState(false);
    const [closeEventId, setCloseEventId] = useState<number | null>(null);
    const [closeEventName, setCloseEventName] = useState<string>('');
    const [closeReason, setCloseReason] = useState('');
    const [closingEvent, setClosingEvent] = useState(false);



    const handleStatusFilterChange = (val: 'all' | 'open' | 'closed') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('event_status');
            else next.set('event_status', val);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleSeverityFilterChange = (val: 'all' | 'critical' | 'warning' | 'info') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('severity');
            else next.set('severity', val);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleEventTypeChange = (val: any | null) => {
        setEventTypeOption(val);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (!val || val.event_type === 'all') next.delete('event_type');
            else next.set('event_type', val.event_type);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };


    const handleDateChange = (type: 'from_date' | 'to_date', val: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val) next.set(type, val);
            else next.delete(type);
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const clearDateFilters = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('from_date');
            next.delete('to_date');
            next.delete('date_preset');
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const handleDatePresetChange = (val: 'all' | '24h' | '7d' | '30d' | 'custom') => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (val === 'all') next.delete('date_preset');
            else next.set('date_preset', val);

            if (val !== 'custom') {
                next.delete('from_date');
                next.delete('to_date');
            }
            next.set('page', '1');
            return next;
        }, { replace: true });
        setCurrentPage(1);
    };

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;


    // Sync from URL params
    useEffect(() => {
        const p = searchParams.get('page');
        const l = searchParams.get('limit');
        const sb = searchParams.get('sort_by');
        const so = searchParams.get('sort_order');
        const et = searchParams.get('event_type');

        if (p) {
            const pageNum = parseInt(p, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) setCurrentPage(pageNum);
        }
        if (l) {
            const limitNum = parseInt(l, 10);
            if (!isNaN(limitNum) && limitNum !== itemsPerPage) setItemsPerPage(limitNum);
        }
        if (sb && sb !== sortBy) setSortBy(sb);
        if (so && (so === 'asc' || so === 'desc') && so !== sortOrder) setSortOrder(so as 'asc' | 'desc');

        if (et) {
            if (!eventTypeOption || eventTypeOption.event_type !== et) {
                setEventTypeOption({ event_type: et, label: formatEventType(et) });
            }
        } else if (eventTypeOption) {
            setEventTypeOption(null);
        }
    }, [searchParams]);



    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
                setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);


    // Fetch data
    const fetchData = async (skip: number = 0, limit: number = itemsPerPage) => {
        try {
            setLoading(true);
            const queryObj: any = level?.id ? { client_id: level.id } : {};
            if (statusFilter !== 'all') queryObj.event_status = statusFilter;
            if (eventTypeOption?.event_type) queryObj.event_type = eventTypeOption.event_type;

            if (debouncedSearch) {
                queryObj.$or = [
                    { message: { $ilike: `%${debouncedSearch}%` } },
                    { event_type: { $ilike: `%${debouncedSearch}%` } },
                    { asset_hostname: { $ilike: `%${debouncedSearch}%` } },
                    { asset_ip: { $ilike: `%${debouncedSearch}%` } },
                    { agent_name: { $ilike: `%${debouncedSearch}%` } },
                    { ticket_number: { $ilike: `%${debouncedSearch}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip, limit,
                sort_by: sortBy, sort_order: sortOrder,
                ...(severityFilter !== 'all' && { severity: severityFilter }),
            };

            if (datePreset === '24h') {
                const d = new Date();
                d.setHours(d.getHours() - 24);
                params.created_after = d.toISOString();
            } else if (datePreset === '7d') {
                const d = new Date();
                d.setDate(d.getDate() - 7);
                params.created_after = d.toISOString();
            } else if (datePreset === '30d') {
                const d = new Date();
                d.setDate(d.getDate() - 30);
                params.created_after = d.toISOString();
            } else if (datePreset === 'custom') {
                if (dateFrom) params.created_after = dateFrom;
                if (dateTo) params.created_before = dateTo;
            }

            const response = await baseapi.get('/events', { params });
            if (response.data.status) {
                setEventsData(response.data.data);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
            } else {
                setEventsData([]); setTotalPages(0); setTotalItems(0);
            }
        } catch (error: any) { showError(error.message); }
        finally { setLoading(false); }
    };


    useEffect(() => {
        fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, debouncedSearch, level, sortBy, sortOrder, statusFilter, severityFilter, dateFrom, dateTo, datePreset, eventTypeOption]);


    const handleRefresh = () => fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);


    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-gray-500 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-orange-500" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-orange-500" />;
    };

    const handleCloseEventClick = (event: EventItem) => {
        setCloseEventId(event.id);
        setCloseEventName(event.asset_hostname || event.agent_name || `Event #${event.id}`);
        setCloseReason('');
        setCloseModalOpen(true);
    };

    const confirmCloseEvent = async () => {
        if (!closeEventId || !closeReason.trim()) return;
        try {
            setClosingEvent(true);
            const res = await baseapi.put(`/events/${closeEventId}/close`, { reason: closeReason.trim() });
            if (res.data.status) {
                showSuccess('Event closed successfully');
                handleRefresh();
            } else {
                showError(res.data.message || 'Failed to close event');
            }
        } catch (error: any) {
            showError(error.message || 'Failed to close event');
        } finally {
            setClosingEvent(false);
            setCloseModalOpen(false);
            setCloseEventId(null);
            setCloseReason('');
        }
    };


    return (
        <div className="bg-gray-900 p-3 min-h-screen pb-20">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-orange-600 p-1.5 rounded shadow-lg"><Activity className="w-4 h-4 text-white" /></div>
                        <div>
                            <h1 className="font-bold text-lg text-gray-200 leading-none">Events</h1>
                        </div>
                        {!loading &&
                            <div className='font-bold text-lg text-gray-100'>
                                ({totalItems})
                            </div>
                        }
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={loading} className="rounded-lg bg-gray-800 border border-gray-700 text-gray-300 p-2 hover:bg-gray-700 transition-colors">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search events..."
                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="relative" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 text-gray-200 transition-all flex items-center gap-2"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-gray-100 uppercase border-b border-gray-800 mb-1 flex items-center justify-between">
                                        <span>Toggle Columns</span>
                                        <button
                                            onClick={toggleAllColumns}
                                            className="text-[9px] text-orange-400 hover:text-orange-300 transition-colors"
                                        >
                                            {columnOptions.every((o) => visibleColumns[o.key])
                                                ? 'Hide All'
                                                : 'Show All'}
                                        </button>
                                    </div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-gray-400 hover:bg-gray-800 rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-orange-500" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-gray-600" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-orange-600 border-orange-600' : 'border-gray-700'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setShowExportModal(true)}
                            className="flex items-center gap-1.5 bg-gray-800 border border-gray-700 rounded-lg px-4 py-1.5 text-[11px] text-white font-bold hover:bg-gray-700 transition-all"
                        >
                            <Download className="w-4 h-4 text-cyan-500" />
                            Export
                        </button>
                    </div>
                </div>


                <div className="flex items-center justify-between px-2 py-1 bg-gray-800/30 rounded-t-xl border border-b-0 border-gray-800 mb-0">
                    <div className="flex items-center gap-4">
                        {/* Status Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-gray-100 uppercase tracking-[0.2em] select-none">Status:</span>
                            <div className="flex items-center bg-gray-900/50 rounded-lg p-1 border border-gray-800/50">
                                {(['all', 'open', 'closed'] as const).map((s) => {
                                    const isSelected = statusFilter === s;
                                    let styles = '';
                                    if (s === 'open') {
                                        styles = isSelected ? 'bg-yellow-600/30 text-yellow-400' : 'text-yellow-400 hover:text-yellow-400';
                                    } else if (s === 'closed') {
                                        styles = isSelected ? 'bg-green-600/20 text-green-400' : 'text-green-400 hover:text-green-400';
                                    } else {
                                        styles = isSelected ? 'bg-gray-700 text-gray-200' : 'text-gray-500 hover:text-gray-400';
                                    }
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => handleStatusFilterChange(s)}
                                            className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-4 w-px bg-gray-700/50" />

                        {/* Type Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-gray-100 uppercase tracking-[0.2em] select-none">Type:</span>
                            <div className="w-44">
                                <SingleSelectLocalSearchApiDropdown
                                    icon={Tag}
                                    apiEndpoint="/events/types"
                                    displayProperty="label"
                                    idProperty="event_type"
                                    searchFields={['label', 'event_type']}
                                    value={eventTypeOption}
                                    onChange={handleEventTypeChange}
                                    placeholder="All Types"
                                    searchInputPlaceholder="Search types..."
                                    className="!p-0"
                                    buttonClassName="!py-1 !px-2 !text-[10px] !bg-gray-900/50 !border-gray-800/50 !shadow-none !h-7"
                                />
                            </div>
                        </div>

                        <div className="h-4 w-px bg-gray-700/50" />

                        {/* Severity Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-gray-100 uppercase tracking-[0.2em] select-none">Severity:</span>
                            <div className="flex items-center bg-gray-900/50 rounded-lg p-1 border border-gray-800/50">
                                {(['all', 'critical', 'warning', 'info'] as const).map((s) => {
                                    const isSelected = severityFilter === s;
                                    let styles = '';
                                    if (s === 'critical') {
                                        styles = isSelected ? 'bg-red-600/30 text-red-400' : 'text-red-400 hover:text-red-400';
                                    } else if (s === 'warning') {
                                        styles = isSelected ? 'bg-yellow-600/30 text-yellow-400' : 'text-yellow-400 hover:text-yellow-400';
                                    } else if (s === 'info') {
                                        styles = isSelected ? 'bg-blue-600/30 text-blue-400' : 'text-blue-400 hover:text-blue-400';
                                    } else {
                                        styles = isSelected ? 'bg-gray-700 text-gray-200' : 'text-gray-500 hover:text-gray-400';
                                    }
                                    return (
                                        <button
                                            key={s}
                                            onClick={() => handleSeverityFilterChange(s)}
                                            className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {s}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Date Filter Group */}


                        {/* Date Preset Filter */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-gray-100 uppercase tracking-[0.2em] select-none">Date:</span>
                            <div className="flex items-center bg-gray-900/50 rounded-lg p-1 border border-gray-800/50">
                                {(['all', '24h', '7d', '30d', 'custom'] as const).map((p) => {
                                    const isSelected = datePreset === p;
                                    let styles = isSelected ? 'bg-gray-700 text-gray-100' : 'text-gray-100 hover:text-gray-100';
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handleDatePresetChange(p)}
                                            className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${styles}`}
                                        >
                                            {p === 'all' ? 'All' : p === '24h' ? '24H' : p === '7d' ? '7D' : p === '30d' ? '30D' : 'Custom'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Date Range - Only shown if Custom preset selected */}
                        {datePreset === 'custom' && (
                            <div className="flex items-center gap-1.5 bg-gray-900/50 rounded-lg p-1 px-3 border border-gray-800/50">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => handleDateChange('from_date', e.target.value)}
                                    className="bg-transparent border-none text-[10px] text-gray-300 outline-none font-mono cursor-pointer"
                                    title="From date"
                                />
                                <span className="text-gray-600 text-[10px]">→</span>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => handleDateChange('to_date', e.target.value)}
                                    className="bg-transparent border-none text-[10px] text-gray-300 outline-none font-mono cursor-pointer"
                                    title="To date"
                                />
                                {(dateFrom || dateTo) && (
                                    <button
                                        onClick={clearDateFilters}
                                        className="ml-1 text-[9px] text-gray-500 hover:text-red-400 font-bold uppercase transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Severity Filter */}
                    </div>
                </div>


                {/* Table */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-b-xl overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-gray-800/80 sticky top-0 z-10">
                                <tr className="border-b border-gray-700/50">
                                    {columns.map((col) => {
                                        if (col.key === 'actions') {
                                            return (
                                                <th key={col.key} className="px-3 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        {col.label}
                                                    </div>
                                                </th>
                                            );
                                        }

                                        const HeaderIcon = col.icon;
                                        
                                        if (col.sortable) {
                                            return (
                                                <th
                                                    key={col.key}
                                                    className="px-3 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group hover:bg-gray-700/50 transition-colors"
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {HeaderIcon && <HeaderIcon size={14} className={col.color} />}
                                                        {col.label}
                                                        <SortIcon field={col.key} />
                                                    </div>
                                                </th>
                                            );
                                        }

                                        return (
                                            <th key={col.key} className="px-3 py-1.5 text-[10px] font-bold text-gray-100 uppercase tracking-widest">
                                                <div className="flex items-center gap-2">
                                                    {HeaderIcon && <HeaderIcon size={14} className={col.color} />}
                                                    {col.label}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700/30">
                                {loading ? (
                                    <tr><td colSpan={columns.length} className="py-16 text-center text-gray-100 font-bold uppercase tracking-widest italic">
                                        <div className="flex items-center justify-center gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                                            Loading events
                                        </div>
                                    </td></tr>
                                ) : eventsData.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-16 text-center text-gray-100 font-bold uppercase tracking-widest italic">
                                        <div className="flex flex-col items-center gap-2">
                                            <Activity className="w-6 h-6 text-gray-600" />
                                            No events found.
                                        </div>
                                    </td></tr>
                                ) : (
                                    eventsData.map((event) => {
                                        const sevConfig = getSeverityConfig(event.severity);
                                        const SevIcon = sevConfig.icon;
                                        const statusConfig = getStatusConfig(event.event_status);

                                        return (
                                            <tr key={event.id} onClick={() => setSelectedEvent(event)} className="hover:bg-gray-800/60 transition-colors group border-b border-gray-700/30 cursor-pointer">
                                                {columns.map((col) => {
                                                    if (col.key === 'asset_hostname') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-gray-100 text-[11px] font-bold leading-tight">
                                                                        {event.asset_hostname || '-'}
                                                                    </span>
                                                                    {event.asset_ip && (
                                                                        <span className="text-gray-100 text-[9px] font-mono flex items-center gap-1">
                                                                            <Network className="w-2.5 h-2.5 text-gray-600" />
                                                                            {event.asset_ip}
                                                                        </span>
                                                                    )}
                                                                    {event.agent_name && (
                                                                        <span className="text-gray-100 text-[9px] flex items-center gap-1">
                                                                            <Bot className="w-2.5 h-2.5 text-gray-600" />
                                                                            {event.agent_name}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'client_name') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <span className="text-emerald-400 font-bold text-[10px] uppercase">
                                                                    {event.client_name || '-'}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'event_type') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <span className="text-gray-100 font-medium text-[11px]">
                                                                    {formatEventType(event.event_type)}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'message') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2 max-w-[350px]">
                                                                <span className="text-gray-100 text-[10px] leading-relaxed line-clamp-2" title={event.message}>
                                                                    {event.message || '-'}
                                                                </span>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'event_status') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusConfig.bg} ${statusConfig.color} border ${statusConfig.border}`}>
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                                                                    {event.event_status || '-'}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'severity') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${sevConfig.bg} ${sevConfig.color} border ${sevConfig.border}`}>
                                                                    <SevIcon className="w-3 h-3" />
                                                                    {event.severity || 'Unknown'}
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'ticket_number') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2" onClick={(e) => { e.stopPropagation(); if (event.ticket_number) window.open(event.ticket_url, '_blank'); }}>
                                                                {event.ticket_number ? (
                                                                    <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border text-blue-400 bg-blue-500/10 border-blue-500/20 uppercase tracking-wider cursor-pointer">
                                                                        <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                                                        #{event.ticket_number}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                                )}
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'created') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2">
                                                                <div className="flex flex-col">
                                                                    <span className="text-gray-100 text-[10px] font-mono flex items-center gap-1.5">
                                                                        <Clock className="w-3 h-3 text-gray-500" />
                                                                        {formatTimestamp(`${event.created}z`)}
                                                                    </span>
                                                                    <span className="text-gray-100 text-[9px] ml-[18px]">
                                                                        {getRelativeTime(`${event.created}z`)}
                                                                    </span>
                                                                </div>
                                                            </td>
                                                        );
                                                    }

                                                    if (col.key === 'actions') {
                                                        return (
                                                            <td key={col.key} className="px-3 py-2 text-center">
                                                                {event.event_status?.toLowerCase() === 'open' ? (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleCloseEventClick(event);
                                                                        }}
                                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 hover:text-red-300 transition-all"
                                                                        title="Close Event"
                                                                    >
                                                                        <XCircle className="w-3.5 h-3.5" />
                                                                        Close
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-gray-600 text-[10px]">—</span>
                                                                )}
                                                            </td>
                                                        );
                                                    }

                                                    return null;
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>


                    {/* Pagination */}
                    <div className="bg-gray-800/50 border-t border-gray-700 px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                            <span className="text-[10px] text-gray-100 font-bold uppercase tracking-widest">
                                <span className="text-gray-100">{totalItems > 0 ? startIndex + 1 : 0}</span> - <span className="text-gray-100">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronsLeft className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">{currentPage}</div>
                            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                            <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30"><ChevronsRight className="w-4 h-4" /></button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Event Detail Slide-out Panel */}
            <AnimatePresence>
                {selectedEvent && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedEvent(null)}
                            className="fixed inset-0 bg-black/40 z-40"
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
                            className="fixed right-0 top-0 h-full w-[620px] bg-slate-900 border-l border-slate-800 z-50 flex flex-col shadow-2xl"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-indigo-500/5">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded bg-indigo-500/15 text-indigo-400">
                                        <Activity className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="text-[12px] font-bold text-slate-100 leading-tight">{selectedEvent.asset_hostname || selectedEvent.agent_name}</p>
                                        <p className="text-[9px] text-gray-100 font-mono">{selectedEvent.asset_ip}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${selectedEvent.event_type === 'camera_offline' ? 'text-rose-400 bg-rose-500/10 border-rose-500/20'
                                        : selectedEvent.event_type === 'camera_online' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
                                            : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
                                        } `}>
                                        {selectedEvent.event_type.replace(/_/g, ' ')}
                                    </span>
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="p-1.5 rounded-lg text-gray-100 hover:text-slate-200 hover:bg-slate-800 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Event Details */}
                            <div className="flex-1 min-h-0 overflow-y-auto custom-scroll p-4 flex flex-col gap-3 pb-20">
                                {/* Event Type Badge */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {selectedEvent.event_status && (
                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${selectedEvent.event_status === 'closed' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-amber-400 bg-amber-500/10 border-amber-500/20'}`}>
                                            {selectedEvent.event_status}
                                        </span>
                                    )}
                                    {selectedEvent.ticket_number && (
                                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-2 py-0.5 rounded border text-blue-400 bg-blue-500/10 border-blue-500/20 uppercase tracking-wider cursor-pointer" onClick={() => window.open(selectedEvent.ticket_url, '_blank')}>
                                            <SquareArrowOutUpRight className="w-3 h-3 flex-shrink-0" />
                                            #{typeof selectedEvent.ticket_number === 'object' ? (selectedEvent.ticket_number as any).ticket_number || (selectedEvent.ticket_number as any).ticket_id || JSON.stringify(selectedEvent.ticket_number) : String(selectedEvent.ticket_number)}
                                        </span>
                                    )}
                                    <span className="text-[9px] text-gray-100 font-mono ml-auto">
                                        {new Date(`${selectedEvent.created}Z`).toLocaleString()}
                                    </span>
                                </div>

                                {/* Message */}
                                <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Message</p>
                                    <p className="text-[11px] text-gray-100 leading-relaxed">{typeof selectedEvent.message === 'object' ? JSON.stringify(selectedEvent.message) : String(selectedEvent.message || '')}</p>
                                </div>

                                {/* Description */}
                                <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Description</p>
                                    <p className="text-[10px] text-gray-100 leading-relaxed">{typeof selectedEvent.description === 'object' ? JSON.stringify(selectedEvent.description) : String(selectedEvent.description || '')}</p>
                                </div>

                                {/* Agent */}
                                <div className="rounded-lg border border-slate-800 bg-slate-800/30 p-3">
                                    <p className="text-[9px] font-bold text-gray-100 uppercase tracking-wider mb-1">Agent</p>
                                    <p className="text-[10px] text-gray-100 truncate" title={typeof selectedEvent.agent_name === 'object' ? JSON.stringify(selectedEvent.agent_name) : String(selectedEvent.agent_name || '')}>{typeof selectedEvent.agent_name === 'object' ? JSON.stringify(selectedEvent.agent_name) : String(selectedEvent.agent_name || '')}</p>
                                </div>

                                {/* Event Data */}
                                {selectedEvent.event_data && (
                                    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 shadow-inner">
                                        <p className="text-[10px] font-bold text-gray-100 uppercase tracking-widest mb-3 border-b border-slate-800/50 pb-2">Event Metrics & Details</p>
                                        {(() => {
                                            try {
                                                let parsedData: any = selectedEvent.event_data;
                                                if (typeof parsedData === 'string') {
                                                    try { parsedData = JSON.parse(parsedData); } catch { }
                                                }
                                                if (typeof parsedData === 'string') {
                                                    try { parsedData = JSON.parse(parsedData); } catch { }
                                                }

                                                const timelineEvents = Array.isArray(parsedData) ? parsedData : (typeof parsedData === 'object' && parsedData !== null ? [parsedData] : []);

                                                if (timelineEvents.length === 0) {
                                                    return (
                                                        <pre className="text-[9px] text-slate-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-slate-900/50 p-3 rounded border border-slate-800/50">
                                                            {typeof selectedEvent.event_data === 'object' ? JSON.stringify(selectedEvent.event_data, null, 2) : String(selectedEvent.event_data)}
                                                        </pre>
                                                    );
                                                }

                                                return (
                                                    <div className="relative border-l border-slate-700 ml-2 pl-3 py-1 space-y-3">
                                                        {timelineEvents.map((evtLog: any, idx: number) => {
                                                            const d = evtLog.data || {};
                                                            const status = evtLog.status || 'info';
                                                            const isClosed = status.toLowerCase() === 'closed';
                                                            const isOpen = status.toLowerCase() === 'open';

                                                            const badgeColor = isClosed ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20'
                                                                : isOpen ? 'text-rose-400 bg-rose-500/10 border border-rose-500/20'
                                                                    : 'text-amber-400 bg-amber-500/10 border border-amber-500/20';

                                                            const dotColor = isClosed ? 'bg-emerald-500 ring-slate-900 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                                                                : isOpen ? 'bg-rose-500 ring-slate-900 shadow-[0_0_8px_rgba(244,63,94,0.8)] animate-pulse'
                                                                    : 'bg-amber-500 ring-slate-900 shadow-[0_0_8px_rgba(245,158,11,0.8)]';

                                                            let eventDescription = '';
                                                            if (isOpen) {
                                                                if (selectedEvent.event_type.includes('offline')) eventDescription = 'System detected asset went offline. Incident opened.';
                                                                else eventDescription = 'Event triggered and incident opened for review.';
                                                            } else if (isClosed) {
                                                                if (selectedEvent.event_type.includes('offline') || selectedEvent.event_type.includes('online')) eventDescription = 'Asset came back online. Incident resolved and closed.';
                                                                else eventDescription = 'Incident was resolved and marked as closed.';
                                                            } else {
                                                                eventDescription = 'Status updated to ' + status;
                                                            }

                                                            const formattedDate = evtLog.created ? new Date(`${evtLog.created}${!evtLog.created.endsWith('Z') && !evtLog.created.includes('+') ? 'Z' : ''}`).toLocaleString(undefined, {
                                                                month: 'short', day: 'numeric', year: 'numeric',
                                                                hour: '2-digit', minute: '2-digit', second: '2-digit'
                                                            }) : 'Unknown Time';

                                                            return (
                                                                <div key={idx} className="relative group">
                                                                    {/* Timeline Dot with Glow */}
                                                                    <div className="absolute -left-[16px] top-2.5 flex items-center justify-center z-10">
                                                                        <div className={`w-1.5 h-1.5 rounded-full ring-4 ${dotColor}`} />
                                                                    </div>

                                                                    {/* Timeline Content Card */}
                                                                    <div className={`bg-slate-900/60 rounded-xl border p-2.5 transition-all duration-300 ${isClosed ? 'border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-950/20'
                                                                        : isOpen ? 'border-rose-500/20 hover:border-rose-500/40 hover:bg-rose-950/20'
                                                                            : 'border-slate-800/80 hover:border-slate-700/80 hover:bg-slate-800/40'
                                                                        }`}>
                                                                        {/* Card Header */}
                                                                        <div className="flex items-center justify-between mb-1.5 border-b border-slate-800/50 pb-1.5">
                                                                            <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${badgeColor}`}>
                                                                                {status}
                                                                            </span>
                                                                            <span className="text-[9px] text-gray-100 font-mono tracking-tight bg-slate-950/50 px-1.5 py-0.5 rounded border border-slate-800/50 shadow-inner">
                                                                                {formattedDate}
                                                                            </span>
                                                                        </div>

                                                                        {/* Description Text */}
                                                                        <div className="flex items-start gap-1.5 mb-1 text-[10px]">
                                                                            <div className={`w-1 h-1 rounded-full mt-1.5 shrink-0 ${isClosed ? 'bg-emerald-500' : isOpen ? 'bg-rose-500' : 'bg-amber-500'}`} />
                                                                            <p className="text-slate-300 font-medium leading-relaxed">{eventDescription}</p>
                                                                        </div>

                                                                        {/* Data details block */}
                                                                        {Object.keys(d).length > 0 && (
                                                                            <div className="bg-slate-950/60 rounded-lg border border-slate-800/50 p-2 mt-2 grid grid-cols-2 gap-y-2 gap-x-3 shadow-inner">
                                                                                {d.offline_start && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Offline Detected</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{new Date(`${d.offline_start}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.online_again && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Back Online</span>
                                                                                        <span className="text-[9px] text-emerald-400 font-mono mt-0.5">{new Date(`${d.online_again}`).toLocaleString()}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.duration_minutes !== undefined && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Total Downtime</span>
                                                                                        <span className="text-[9px] text-indigo-400 font-bold mt-0.5">{d.duration_minutes} Minutes</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.failure_reason && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Root Cause</span>
                                                                                        <span className="text-[9px] text-rose-400 font-mono mt-0.5 tabular-nums">{d.failure_reason.replace(/_/g, ' ')}</span>
                                                                                    </div>
                                                                                )}
                                                                                {d.ip_address && (
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">Source IP</span>
                                                                                        <span className="text-[9px] text-gray-100 font-mono mt-0.5">{d.ip_address}</span>
                                                                                    </div>
                                                                                )}

                                                                                {Object.entries(d)
                                                                                    .filter(([k]) => !['offline_start', 'online_again', 'duration_minutes', 'failure_reason', 'ip_address', 'ticket_description_html', 'warning_issues', 'critical_issues', 'assets'].includes(k))
                                                                                    .map(([k, v]) => (
                                                                                        <div className="flex flex-col" key={k}>
                                                                                            <span className="text-[8px] text-gray-100 font-bold uppercase tracking-widest shrink-0">{k.replace(/_/g, ' ')}</span>
                                                                                            <span className="text-[9px] text-gray-100 font-mono mt-0.5 overflow-hidden text-ellipsis truncate" title={typeof v === 'object' ? JSON.stringify(v) : String(v)}>
                                                                                                {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (typeof v === 'object' ? JSON.stringify(v) : String(v))}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))
                                                                                }

                                                                                {/* System Issues: Critical */}
                                                                                {(d.critical_issues && Array.isArray(d.critical_issues) && d.critical_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-rose-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertTriangle className="w-3.5 h-3.5" />
                                                                                            Critical Issues ({d.critical_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.critical_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-rose-950/20 border border-rose-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-rose-300 leading-tight">{iss.issue}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-rose-500/20 text-rose-300 rounded uppercase shrink-0">{iss.category}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{iss.description}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* System Issues: Warning */}
                                                                                {(d.warning_issues && Array.isArray(d.warning_issues) && d.warning_issues.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                                                            Warning Issues ({d.warning_issues.length})
                                                                                        </span>
                                                                                        <div className="flex flex-col gap-1.5">
                                                                                            {d.warning_issues.map((iss: any, i: number) => (
                                                                                                <div key={i} className="bg-amber-950/20 border border-amber-900/30 rounded p-2.5">
                                                                                                    <div className="flex items-start justify-between mb-1 gap-2">
                                                                                                        <span className="text-[10px] font-bold text-amber-300 leading-tight">{iss.issue}</span>
                                                                                                        <span className="text-[8px] px-1.5 py-0.5 bg-amber-500/20 text-amber-300 rounded uppercase shrink-0">{iss.category}</span>
                                                                                                    </div>
                                                                                                    <p className="text-[9px] text-gray-400 leading-relaxed">{iss.description}</p>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Discovered / Imported Assets */}
                                                                                {(d.assets && Array.isArray(d.assets) && d.assets.length > 0) && (
                                                                                    <div className="col-span-2 mt-2 space-y-2 border-t border-slate-800/50 pt-3">
                                                                                        <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                                                                            <Server className="w-3.5 h-3.5" />
                                                                                            Assets ({d.assets.length})
                                                                                        </span>
                                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                                                            {d.assets.map((asset: any, i: number) => (
                                                                                                <div key={i} className="bg-emerald-950/20 border border-emerald-900/30 rounded p-2.5 flex items-center justify-between">
                                                                                                    <div className="flex flex-col gap-0.5">
                                                                                                        <span className="text-[10px] font-bold text-emerald-300 leading-tight truncate max-w-[140px]" title={asset.name}>{asset.name || 'Unknown Asset'}</span>
                                                                                                        <span className="text-[9px] text-gray-400 font-mono">{asset.ip_address || 'No IP'}</span>
                                                                                                    </div>
                                                                                                    <span className="text-[8px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded uppercase shrink-0">{asset.asset_type || 'Unknown'}</span>
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            } catch (e) {
                                                return (
                                                    <pre className="text-[9px] text-slate-400 font-mono leading-relaxed whitespace-pre-wrap break-all bg-slate-900/50 p-3 rounded border border-slate-800/50">
                                                        {typeof selectedEvent.event_data === 'object' ? JSON.stringify(selectedEvent.event_data, null, 2) : String(selectedEvent.event_data)}
                                                    </pre>
                                                );
                                            }
                                        })()}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Close Event Modal */}
            {closeModalOpen && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}>
                    <div
                        className="bg-gray-900 border border-gray-700/50 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full max-w-md overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 bg-red-500/20 rounded-lg">
                                    <XCircle className="w-4 h-4 text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white">Close Event</h3>
                                    <p className="text-[10px] text-gray-500 font-medium">{closeEventName}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}
                                disabled={closingEvent}
                                className="text-gray-500 hover:text-white transition-colors bg-gray-800/50 p-1.5 rounded-lg border border-gray-700/30 hover:border-gray-600 disabled:opacity-50"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="text-[11px] font-bold text-gray-300 uppercase tracking-wider mb-2 block">Reason for closing</label>
                                <textarea
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    placeholder="Enter the reason for closing this event..."
                                    className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-3 py-2.5 text-[12px] text-white focus:border-orange-500 outline-none transition-all placeholder:text-gray-600 resize-none h-28"
                                    disabled={closingEvent}
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-2 pt-1">
                                <button
                                    onClick={() => { if (!closingEvent) setCloseModalOpen(false); }}
                                    disabled={closingEvent}
                                    className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmCloseEvent}
                                    disabled={closingEvent || !closeReason.trim()}
                                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {closingEvent ? (
                                        <>
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            Closing...
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-3.5 h-3.5" />
                                            Close Event
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <AgentExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                templateName="events"
                clientId={level?.id}
            />
        </div>
    );
};


export default EventsTable;
